import { useMemo, useState } from "react";
import { Table, Spinner, Container, Form } from "react-bootstrap";
import styles from "./results-table.module.scss";
import useStore, { ResultsData } from "../../store";

const serchableFields = ["name", "bib"];
const selectableFields: Record<string, string[]> = {
  category: [],
};

const sortText = (data: string[], direction: "asc" | "desc" = "asc") => {
  return data.sort((a, b) => {
    if (direction === "asc") {
      return a.localeCompare(b);
    } else {
      return b.localeCompare(a);
    }
  });
};

const ResultsTable = () => {
  const { resultsData, isResultsDataLoading, resultSets } = useStore();
  const [sortConfig, setSortConfig] = useState({
    key: "durationHumanReadible",
    direction: "asc",
  });
  const [searchTerms, setSearchTerms] = useState<{
    [key: string]: string | undefined;
  }>({});

  resultsData.forEach((result) => {
    (Object.entries(result) || []).forEach(([resultKey, resultValue]) => {
      if (
        resultValue &&
        selectableFields.hasOwnProperty(resultKey) &&
        !selectableFields[resultKey].includes(resultValue)
      ) {
        selectableFields[resultKey].push(resultValue);
      }
    });
  });

  const activeResultSet = resultSets.find((set) => set.isActive);
  const activeResultSetColumns = activeResultSet?.tables[0]?.columns || {};

  const assignGrouppedPositionToParticipants = (field: string) => {
    const participantGroups = resultsData.reduce(
      (groups: Record<string, any>, participant: Record<string, any>) => {
        const category = participant[field];

        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(participant);
        return groups;
      },
      {}
    );

    (Object.keys(participantGroups) || []).forEach((category) => {
      participantGroups[category].sort(
        //@ts-ignore
        (a, b) => a.rankingPosition - b.rankingPosition
      );
    });
    //@ts-ignore
    const newResults = [];

    (Object.entries(participantGroups) || []).forEach(
      ([group, participants]) => {
        //@ts-ignore
        (participants || []).forEach((participant, key: number) => {
          newResults.push({
            ...participant,
            grouppedPosition: key + 1,
          });
        });
      }
    );

    //@ts-ignore
    return newResults;
  };

  const columnArrayFromObject = Object.entries(activeResultSetColumns).map(
    ([key, value]) => ({
      name: key,
      dataKey: value,
    })
  );

  const formatColumnValue = (dataKey: string, result: any) => {
    const value = result[dataKey];

    switch (dataKey) {
      case "name":
        return String(formatParticipantName(value, result?.grouppedPosition || -1))?.replace(/\n/g, '<br>');

      case "teamName":
        return String(formatParticipantName(value, result['rankingPosition'] || -1))?.replace(/\n/g, '<br>');

      default:
        return String(value)?.replace(/\n/g, '<br>');
    }
  };

  const formatParticipantName = (name: string, grouppedPosition: number) => {
    switch (grouppedPosition) {
      case 1:
        return `🥇 ${name}`;
      case 2:
        return `🥈 ${name}`;
      case 3:
        return `🥉 ${name}`;
      default:
        return name;
    }
  };

    const sortedData = (columnKey: string, dataToSort: ResultsData[]) => {
      const sortedData = [...dataToSort].sort((a, b) => {
        //@ts-ignore
        const aValue = a[columnKey]?.toString().trim() || "";
        //@ts-ignore
        const bValue = b[columnKey]?.toString().trim() || "";

        if (aValue === "-") return 1;
        if (bValue === "-") return -1;

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });

      //@ts-ignore
      return sortedData;
    };

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "";
  };

  const handleOnSearch = (dataKey: string, searchValue?: string) => {
    setSearchTerms({
      ...searchTerms,
      [dataKey]: searchValue,
    });
  };

  const filteredData = (data: ResultsData[]) => {
    return data.filter((row) => {
      return Object.entries(searchTerms).every(([key, value]) => {
        //@ts-ignore
        if (!value || !row[key]) {
          return true;
        }
        //@ts-ignore
        return row[key].toLowerCase().includes(value.toLowerCase());
      });
    });
  };

  const filteredAndSortedData = useMemo(() => {
    const groupCategoryKey =
      activeResultSet?.tables?.[0]?.sort?.[0] ?? undefined;

    const participantsWithGrouppedRanking = groupCategoryKey
      ? assignGrouppedPositionToParticipants(groupCategoryKey)
      : resultsData;

    const filtered = filteredData(participantsWithGrouppedRanking);
    return sortedData(sortConfig.key, filtered);
  }, [resultsData, searchTerms, sortConfig]);

  return (
    <Container fluid="xxl" className={styles.container}>
      {!!Object.keys(activeResultSetColumns).length && (
        <Table striped bordered hover className={styles.results}>
          <thead>
            <tr>
              {columnArrayFromObject.map((column) => (
                <th
                  onClick={() =>
                    setSortConfig({
                      key: column.dataKey,
                      direction:
                        sortConfig.direction === "desc" ? "asc" : "desc",
                    })
                  }
                  key={`th${column.name}`}
                >
                  <div className={styles.thContentWrapper}>
                    {column.name} {getSortIcon(column.dataKey)}
                    {serchableFields.includes(column.dataKey) && (
                      <Form.Control
                        type="text"
                        placeholder={`Search ${column.name}`}
                        value={searchTerms[column.dataKey] || ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          handleOnSearch(column.dataKey, e.target.value)
                        }
                      />
                    )}
                    {selectableFields.hasOwnProperty(column.dataKey) && (
                      <Form.Select
                        value={searchTerms[column.dataKey] || ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          handleOnSearch(column.dataKey, e.target.value);
                        }}
                      >
                        <option></option>
                        {sortText(selectableFields[column.dataKey]).map(
                          (value) => (
                            <option key={`sel-${value}`}>{value}</option>
                          )
                        )}
                      </Form.Select>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {!isResultsDataLoading && (
            <tbody>
              {/* @ts-ignore */}
              {!!filteredAndSortedData.length &&
                filteredAndSortedData.map((result, index) => (
                  <tr key={`${result.name}-tr-${index}`}>
                    {columnArrayFromObject.map((column) => {
                      //@ts-ignore
                      const raw = formatColumnValue(column.dataKey, result);
                      const formatedColumnValue = raw == null || raw == 'undefined' ? "" : raw;
                      return (
                        <td
                          key={`${result.name}-tr-${index}-td${column.name}`}
                          className={styles.td}
                          dangerouslySetInnerHTML={{ __html: formatedColumnValue }}
                        >
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          )}
        </Table>
      )}

      {isResultsDataLoading && (
        <div className="d-flex align-items-center justify-content-center">
          <Spinner variant="light" />
        </div>
      )}
    </Container>
  );
};

export default ResultsTable;
